import React, { useState } from "react";
import { Copy, CheckCircle, Link2, Users, Clock, Hash } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./select";

import { useToast } from "./use-toast";
import { trpc } from "@/utils/trpc";

interface ShareDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	workspaceId?: string;
	channelId?: string;
	channelTitle?: string;
}

export const ShareDialog: React.FC<ShareDialogProps> = ({
	open,
	onOpenChange,
	workspaceId,
	channelId,
	channelTitle,
}) => {
	const [inviteUrl, setInviteUrl] = useState<string>("");
	const [copied, setCopied] = useState(false);
	const [expiresIn, setExpiresIn] = useState("24");
	const [maxUses, setMaxUses] = useState("10");
	const [permissions, setPermissions] = useState<"r" | "rw">("rw");
	const { toast } = useToast();

	const createInvitation = useMutation(
		trpc.invitation.createChannelInvitation.mutationOptions({
			onSuccess: (data) => {
				const fullUrl = `${window.location.origin}${data.inviteUrl}`;
				setInviteUrl(fullUrl);
				toast({
					title: "Invitation created",
					description: "Share this link with others to give them access.",
				});
			},
			onError: (error) => {
				toast({
					title: "Failed to create invitation",
					description: error.message,
					variant: "destructive",
				});
			},
		})
	);

	const handleCreateLink = () => {
		if (!channelId) return;

		createInvitation.mutate({
			channelId: Number(channelId),
			invitationType: "channel_member",
			permissions,
			expiresIn: Number(expiresIn),
			maxUses: Number(maxUses),
		});
	};

	const handleCopy = async () => {
		if (!inviteUrl) return;

		try {
			await navigator.clipboard.writeText(inviteUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
			toast({
				title: "Copied to clipboard",
				description: "The invitation link has been copied.",
			});
		} catch (error) {
			toast({
				title: "Failed to copy",
				description: "Please try copying the link manually.",
				variant: "destructive",
			});
		}
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			// Reset state when closing
			setInviteUrl("");
			setCopied(false);
			setExpiresIn("24");
			setMaxUses("10");
			setPermissions("rw");
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Link2 className="h-5 w-5" />
						Share {channelTitle || "this view"}
					</DialogTitle>
					<DialogDescription>
						Create a shareable link to invite others to collaborate.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{!inviteUrl ? (
						<>
							<div className="space-y-2">
								<Label
									htmlFor="permissions"
									className="flex items-center gap-2"
								>
									<Users className="h-4 w-4" />
									Permissions
								</Label>
								<Select
									value={permissions}
									onValueChange={(value: "r" | "rw") => setPermissions(value)}
								>
									<SelectTrigger id="permissions">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="r">View only</SelectItem>
										<SelectItem value="rw">View and edit</SelectItem>
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<Label htmlFor="expires" className="flex items-center gap-2">
									<Clock className="h-4 w-4" />
									Expires after (hours)
								</Label>
								<Select value={expiresIn} onValueChange={setExpiresIn}>
									<SelectTrigger id="expires">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="1">1 hour</SelectItem>
										<SelectItem value="6">6 hours</SelectItem>
										<SelectItem value="24">24 hours</SelectItem>
										<SelectItem value="48">2 days</SelectItem>
										<SelectItem value="168">1 week</SelectItem>
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<Label htmlFor="uses" className="flex items-center gap-2">
									<Hash className="h-4 w-4" />
									Maximum uses
								</Label>
								<Select value={maxUses} onValueChange={setMaxUses}>
									<SelectTrigger id="uses">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="1">1 use</SelectItem>
										<SelectItem value="5">5 uses</SelectItem>
										<SelectItem value="10">10 uses</SelectItem>
										<SelectItem value="25">25 uses</SelectItem>
										<SelectItem value="100">100 uses</SelectItem>
									</SelectContent>
								</Select>
							</div>

							<Button
								onClick={handleCreateLink}
								disabled={!channelId || createInvitation.isPending}
								className="w-full"
							>
								{createInvitation.isPending ? "Creating..." : "Create Link"}
							</Button>
						</>
					) : (
						<>
							<div className="space-y-2">
								<Label>Invitation Link</Label>
								<div className="flex gap-2">
									<Input
										value={inviteUrl}
										readOnly
										className="font-mono text-xs"
										onClick={(e) => e.currentTarget.select()}
									/>
									<Button
										size="icon"
										variant={copied ? "default" : "outline"}
										onClick={handleCopy}
									>
										{copied ? (
											<CheckCircle className="h-4 w-4" />
										) : (
											<Copy className="h-4 w-4" />
										)}
									</Button>
								</div>
								<p className="text-muted-foreground text-xs">
									This link expires in {expiresIn} hours and can be used{" "}
									{maxUses} times.
								</p>
							</div>

							<div className="flex gap-2">
								<Button
									variant="outline"
									onClick={() => setInviteUrl("")}
									className="flex-1"
								>
									Create New Link
								</Button>
								<Button
									onClick={() => handleOpenChange(false)}
									className="flex-1"
								>
									Done
								</Button>
							</div>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};
